import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Select, Avatar, Space, Spin } from 'antd';
import {
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  CalendarOutlined,
  FileOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  RocketOutlined,
  CarOutlined,
  ToolOutlined,
  BookOutlined,
  DesktopOutlined,
  SendOutlined,
  CompassOutlined,
  TeamOutlined,
  EnvironmentOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  ScheduleOutlined,
  FlagOutlined,
  StarOutlined,
  FireOutlined,
} from '@ant-design/icons';
import apiClient from '@/api/client';
import type { WidgetProps } from '../registry';

// Map icon names from backend to Ant Design icons
const ICON_MAP: Record<string, React.ReactNode> = {
  // Status icons
  'check-circle': <CheckCircleOutlined />,
  check: <CheckCircleOutlined />,
  completed: <CheckCircleOutlined />,
  clock: <ClockCircleOutlined />,
  'clock-circle': <ClockCircleOutlined />,
  scheduled: <ClockCircleOutlined />,
  edit: <EditOutlined />,
  draft: <EditOutlined />,
  'close-circle': <CloseCircleOutlined />,
  close: <CloseCircleOutlined />,
  cancelled: <CloseCircleOutlined />,
  sync: <SyncOutlined spin />,
  'in-progress': <SyncOutlined spin />,
  loading: <SyncOutlined spin />,
  calendar: <CalendarOutlined />,
  file: <FileOutlined />,
  warning: <ExclamationCircleOutlined />,
  exclamation: <ExclamationCircleOutlined />,
  'minus-circle': <MinusCircleOutlined />,
  pause: <PauseCircleOutlined />,
  play: <PlayCircleOutlined />,
  confirmed: <CheckCircleOutlined />,

  // Event type icons
  rocket: <RocketOutlined />,
  'building-hours': <ScheduleOutlined />,
  flight: <SendOutlined />,
  'flight-instructor': <TeamOutlined />,
  'flight-location': <EnvironmentOutlined />,
  cargo: <SendOutlined />,
  'cargo-flight': <SendOutlined />,
  taxi: <CarOutlined />,
  'taxi-flight': <CarOutlined />,
  ground: <BookOutlined />,
  'ground-training': <BookOutlined />,
  simulator: <DesktopOutlined />,
  'simulator-session': <DesktopOutlined />,
  maintenance: <ToolOutlined />,
  'maintenance-check': <ToolOutlined />,
  compass: <CompassOutlined />,
  send: <SendOutlined />,
  team: <TeamOutlined />,
  environment: <EnvironmentOutlined />,
  location: <EnvironmentOutlined />,
  book: <BookOutlined />,
  desktop: <DesktopOutlined />,
  tool: <ToolOutlined />,
  car: <CarOutlined />,
  schedule: <ScheduleOutlined />,
  flag: <FlagOutlined />,
  star: <StarOutlined />,
  fire: <FireOutlined />,
  thunder: <ThunderboltOutlined />,
  safety: <SafetyOutlined />,

  // Priority icons
  high: <FireOutlined />,
  medium: <FlagOutlined />,
  low: <MinusCircleOutlined />,
  urgent: <ThunderboltOutlined />,
  critical: <ExclamationCircleOutlined />,

  // Flight type icons
  vfr: <CompassOutlined />,
  ifr: <SafetyOutlined />,
  visual: <CompassOutlined />,
  instrument: <SafetyOutlined />,
  night: <StarOutlined />,
  'night-vfr': <StarOutlined />,
  'night-ifr': <StarOutlined />,
  training: <BookOutlined />,
  'training-flight': <BookOutlined />,

  // Airport icon (red pin)
  pin: <EnvironmentOutlined />,
  airport: <EnvironmentOutlined />,
  'location-pin': <EnvironmentOutlined />,
};

interface DropdownOption {
  value: string;
  label: string;
  image?: string | null;
  icon?: string | null; // Icon name from backend (e.g., 'check-circle', 'clock', 'edit')
  color?: string | null; // Color for icon or color dot
}

export const SimpleDropdownWidget: React.FC<WidgetProps> = ({
  widget,
  onChange,
  onBlur,
  error,
  warning,
}) => {
  // Initialize options with selected_option if available (for pre-populated dropdowns)
  const getInitialOptions = (): DropdownOption[] => {
    if (widget.selected_option) {
      const opt = widget.selected_option as {
        id: string;
        display_name: string;
        image_url?: string;
        icon?: string;
        color?: string;
      };
      return [
        {
          value: opt.id,
          label: opt.display_name,
          image: opt.image_url || null,
          icon: opt.icon || null,
          color: opt.color || null,
        },
      ];
    }
    return [];
  };

  const [options, setOptions] = useState<DropdownOption[]>(getInitialOptions);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Use ref to always have latest url_params (avoids stale closure in debounce)
  const urlParamsRef = useRef(widget.source_data?.url_params);
  urlParamsRef.current = widget.source_data?.url_params;

  const sourceData = widget.source_data;
  const isApiSource = sourceData?.options_source === 'API';
  const isServerSearch = widget.search_mode === 'server';
  const searchParam = widget.search_param || 'search';
  const searchMinChars = widget.search_min_chars ?? 2;
  const debounceMs = sourceData?.debounce_ms ?? 300;

  // Pre-populated selected option from backend (for prepopulated dropdowns)
  const selectedOption = widget.selected_option as
    | {
        id: string;
        display_name: string;
        image_url?: string;
        icon?: string;
        color?: string;
      }
    | undefined;

  // Build URL with url_params and optional search term
  const buildApiUrl = useCallback(
    (
      baseUrl: string,
      urlParams?: Record<string, any>,
      searchTerm?: string
    ): string => {
      const params = new URLSearchParams();

      // Add search term if provided and meets min chars
      if (searchTerm && searchTerm.length >= searchMinChars) {
        params.append(searchParam, searchTerm);
      }

      // Add url_params (already resolved by backend)
      if (urlParams) {
        for (const [key, value] of Object.entries(urlParams)) {
          if (value != null) {
            params.append(key, String(value));
          }
        }
      }

      const queryString = params.toString();
      if (!queryString) {
        return baseUrl;
      }

      return baseUrl + (baseUrl.includes('?') ? '&' : '?') + queryString;
    },
    [searchParam, searchMinChars]
  );

  // Map API response to options format
  const mapResponseToOptions = useCallback(
    (data: any): DropdownOption[] => {
      const valueFormat = sourceData?.value_format || {};
      const idField = valueFormat.id || 'id';
      const displayField = valueFormat.display_name || 'name';
      const imageField = valueFormat.image || null;
      const iconField = valueFormat.icon || 'icon';
      const colorField = valueFormat.color || 'color';

      // Handle both array and paginated responses
      const items = Array.isArray(data)
        ? data
        : data.items || data.results || [];

      return items.map((item: any) => ({
        value: item[idField],
        label: item[displayField],
        image: imageField ? item[imageField] : null,
        icon: item[iconField] || null,
        color: item[colorField] || null,
      }));
    },
    [sourceData?.value_format]
  );

  // Fetch options from API
  const fetchOptions = useCallback(
    async (searchTerm?: string) => {
      if (!sourceData?.url) return;

      setLoading(true);
      setFetchError(null);

      try {
        // Use urlParamsRef.current to always get latest url_params (avoids stale closure)
        const apiUrl = buildApiUrl(
          sourceData.url,
          urlParamsRef.current,
          searchTerm
        );
        const response = await apiClient.get(apiUrl);
        let mappedOptions = mapResponseToOptions(response.data);

        // Preserve selected_option if it's not in the fetched results
        // This ensures pre-populated dropdowns always show the correct label
        if (
          selectedOption &&
          !mappedOptions.some((opt) => opt.value === selectedOption.id)
        ) {
          mappedOptions = [
            {
              value: selectedOption.id,
              label: selectedOption.display_name,
              image: selectedOption.image_url || null,
              icon: selectedOption.icon || null,
              color: selectedOption.color || null,
            },
            ...mappedOptions,
          ];
        }

        setOptions(mappedOptions);
      } catch (err) {
        console.error('Failed to fetch dropdown options:', err);
        setFetchError('Failed to load options');
      } finally {
        setLoading(false);
      }
    },
    [sourceData?.url, buildApiUrl, mapResponseToOptions, selectedOption]
  );

  // Debounced search for server-side mode
  const debouncedSearch = useCallback(
    (searchTerm: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        fetchOptions(searchTerm);
      }, debounceMs);
    },
    [fetchOptions, debounceMs]
  );

  // Initial load and reload when url_params change
  useEffect(() => {
    if (isApiSource && sourceData?.url) {
      // Fetch initial options on mount for both local and server search
      fetchOptions();
    } else {
      // Static source - use options from widget definition
      const rawOptions = widget.options || sourceData?.options || [];

      const mappedOptions = rawOptions.map((opt: any) => {
        if (typeof opt === 'string') {
          return {
            value: opt,
            label: opt,
            image: null,
            icon: null,
            color: null,
          };
        }
        return {
          value: opt.value ?? opt.id ?? opt.key,
          label:
            opt.label || opt.text || opt.name || opt.display_name || opt.value,
          image: opt.image || opt.image_url || null,
          icon: opt.icon || null,
          color: opt.color || null,
        };
      });

      setOptions(mappedOptions);
    }

    // Cleanup debounce on unmount
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isApiSource,
    sourceData?.url,
    sourceData?.options,
    widget.options,
    JSON.stringify(sourceData?.url_params),
  ]);

  // Handle search input
  const handleSearch = (searchTerm: string) => {
    if (!isServerSearch) {
      // Local mode: filtering handled by filterOption prop
      return;
    }

    // Server mode: fetch from API with search term
    // Only search if we have enough characters - don't refetch for empty string
    // (empty fetch only happens on initial load via useEffect)
    if (searchTerm.length >= searchMinChars) {
      debouncedSearch(searchTerm);
    }
  };

  // Custom option renderer with icon/avatar/color support
  const renderOption = (option: DropdownOption) => {
    // Priority: icon > image > color dot > plain text

    // 1. Icon from backend (for statuses, categories, etc.)
    if (option.icon) {
      const iconElement = ICON_MAP[option.icon.toLowerCase()];
      if (iconElement) {
        return (
          <Space>
            <span
              style={{ color: option.color || 'inherit', fontSize: '16px' }}
            >
              {iconElement}
            </span>
            {option.label}
          </Space>
        );
      }
    }

    // 2. Image/Avatar (for people, airplanes, etc.)
    if (option.image) {
      return (
        <Space>
          <Avatar size="small" src={option.image} />
          {option.label}
        </Space>
      );
    }

    // 3. Color dot (for simple color-coded options)
    if (option.color) {
      return (
        <Space>
          <span
            style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: option.color,
            }}
          />
          {option.label}
        </Space>
      );
    }

    // 4. Plain text
    return option.label;
  };

  // For server-side search, disable local filtering
  const filterOption = isServerSearch
    ? false
    : (input: string, option: any) => {
        const opt = options.find((o) => o.value === option?.value);
        return (opt?.label ?? '').toLowerCase().includes(input.toLowerCase());
      };

  return (
    <div className="rul-widget-field">
      {widget.label && (
        <label className="rul-widget-field-label">
          {widget.label}
          {widget.required && <span className="required">*</span>}
        </label>
      )}
      <Select
        value={widget.value || undefined}
        placeholder={widget.placeholder || 'Select...'}
        onChange={(value) => {
          onChange?.(widget.name, value);
          // Select triggers blur on selection, so also call onBlur
          onBlur?.(widget.name, value);
        }}
        onSearch={widget.searchable !== false ? handleSearch : undefined}
        status={error || fetchError ? 'error' : warning ? 'warning' : ''}
        size="large"
        style={{ width: '100%' }}
        allowClear={widget.allow_clear !== false}
        showSearch={widget.searchable !== false}
        filterOption={filterOption}
        disabled={!widget.enabled || (loading && !isServerSearch)}
        loading={loading}
        notFoundContent={loading ? <Spin size="small" /> : 'No options'}
        autoClearSearchValue={!isServerSearch}
        labelRender={({ value }) => {
          // Find the option to render icon/avatar in closed state
          const opt = options.find((o) => o.value === value);
          if (!opt) return value;
          return renderOption(opt);
        }}
      >
        {options.map((opt) => (
          <Select.Option key={opt.value} value={opt.value} label={opt.label}>
            {renderOption(opt)}
          </Select.Option>
        ))}
      </Select>
      {widget.help_text && !error && !fetchError && !warning && (
        <div className="rul-widget-help">{widget.help_text}</div>
      )}
      {(error || fetchError) && (
        <div className="rul-widget-error">{error || fetchError}</div>
      )}
      {warning && !error && !fetchError && (
        <div className="rul-widget-warning">{warning}</div>
      )}
    </div>
  );
};
